// Ajustes > Avisos e alarmes (0.67, texto 05 D3/G1): como o celular avisa no app todo.
// Som e vibração padrão, "Não perturbe", antecedência das contas a pagar e o teste do alerta
// (o teste e a explicação sobre bateria e notificações vieram de My Planner > Alertas dos compromissos).
import { useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { garantirCanaisVibracao, testarAlerta, type ResultadoTeste } from '../../notifications';
import { setDoc, useDoc } from '../../store/store';
import { C, alpha } from '../../components/theme';
import { PickerModal } from '../../components/Pickers';
import { DIAS_ANTES } from '../../components/Caixinhas';
import { MODOS_AVISO, TIPOS_VIBRACAO, modoReal, modoVibra, sentirModo, sentirVibracao, somDoApp, vibracaoPropria } from '../../lib/avisos';
import { SONS, ouvirSom, temSonsProprios } from '../../lib/sons';
import { ResultadoDoTeste, abrirConfiguracoes } from '../../components/AlertasEditor';
import { t } from '../../lib/i18n';
import { horaTela } from '../../lib/date';
import type { ModoAviso, TipoVibracao } from '../../store/types';
import { BotaoAcao, Bloco, Dica, Divisor, LinhaChave, LinhaEscolha, LinhaOpcoes, useTamCaixas } from '../../components/ajustes/Linhas';
import { AlvoGuia } from '../../components/guia/Destaque';

const NP_PADRAO = { on: false, de: '22:00', ate: '07:00' };
const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const dataDe = (h: string) => { const d = new Date(); const [a, b] = h.split(':').map(Number); d.setHours(a || 0, b || 0, 0, 0); return d; };

export default function Avisos({ cor }: { cor: string }) {
  const aj = useDoc('ajustes');
  // texto 10 (A1): sem escolha = Som e vibração; um modo que este app não faz aparece como Som e vibração
  const modo: ModoAviso = modoReal(aj.avisos?.modo ?? 'ambos');
  const vib: TipoVibracao = aj.avisos?.vibracao ?? 'dupla';
  const np = aj.avisos?.naoPerturbe ?? NP_PADRAO;
  const [hora, setHora] = useState<'de' | 'ate' | null>(null);
  const mudarNp = (x: Partial<typeof np>) => setDoc('ajustes', d => ({ ...d, avisos: { ...d.avisos, naoPerturbe: { ...NP_PADRAO, ...d.avisos?.naoPerturbe, ...x } } }));
  return (<>
    <Bloco titulo={t('Padrão do app')}>
      <ListaComSentir cor={cor} valor={modo} opcoes={MODOS_AVISO.map(([k, l]) => [k, t(l)] as [ModoAviso, string])}
        onEscolher={v => setDoc('ajustes', d => ({ ...d, avisos: { ...d.avisos, modo: v } }))}
        podeSentir={modoVibra} onSentir={v => sentirModo(v, vib)} />
      <Dica>{t('Vale para todas as abas. Cada aba pode ter o seu em Ajustes > Abas.')}</Dica>
    </Bloco>

    {/* texto 10 (B): só onde o aviso de verdade usa o mesmo desenho do ▶ Sentir; nos outros, a opção some */}
    {vibracaoPropria && (
      <Bloco titulo={t('Tipo de vibração')}>
        <ListaComSentir cor={cor} valor={vib} opcoes={TIPOS_VIBRACAO.map(([k, l]) => [k, t(l)] as [TipoVibracao, string])}
          onEscolher={v => { setDoc('ajustes', d => ({ ...d, avisos: { ...d.avisos, vibracao: v } })); garantirCanaisVibracao(v); sentirVibracao(v); }}
          podeSentir={() => true} onSentir={sentirVibracao} />
        <Dica>{t('Vale para todos os avisos que vibram (Vibração e Som e vibração).')}</Dica>
      </Bloco>
    )}

    {/* texto 10 (A1) / 11g: sons próprios, só no app instalado que já tem os sons */}
    {temSonsProprios() && (
      <Bloco titulo={t('Som do alarme')}>
        <ListaComSentir cor={cor} valor={somDoApp() ?? SONS[0].id} opcoes={SONS.map(x => [x.id, t(x.nome)] as [string, string])}
          onEscolher={v => { setDoc('ajustes', d => ({ ...d, avisos: { ...d.avisos, som: v } })); ouvirSom(v); }}
          podeSentir={() => true} onSentir={v => { ouvirSom(v); }} rotulo={t('Ouvir')} />
        <Dica>{t('Toca nos avisos em "Alarme (som)". "Som e vibração" usa o som de notificação do celular.')}</Dica>
      </Bloco>
    )}

    <Bloco titulo={t('Não perturbe')}>
      <AlvoGuia id="ajustes.avisos.naoPerturbe">
      <LinhaChave icone="🌙" titulo={t('Não perturbe')} cor={cor} valor={np.on} onMudar={v => mudarNp({ on: v })}
        dica={t('Nesse horário o app não toca nem vibra. Os avisos continuam chegando, em silêncio.')} />
      </AlvoGuia>
      {np.on && (<>
        <Divisor />
        <LinhaEscolha icone="⏱️" titulo={t('De')} valor={horaTela(np.de)} onPress={() => setHora('de')} />
        <Divisor />
        <LinhaEscolha icone="⏰" titulo={t('Até')} valor={horaTela(np.ate)} onPress={() => setHora('ate')} />
      </>)}
    </Bloco>

    <Bloco titulo={t('Contas a pagar')}>
      <LinhaOpcoes icone="💸" titulo={t('Avisar antes do vencimento')} valor={aj.contasAntes ?? 3}
        opcoes={DIAS_ANTES.map(n => [n, t('{n} dia(s) antes', { n })] as [number, string])}
        onEscolher={n => setDoc('ajustes', d => ({ ...d, contasAntes: n }))}
        dica={t('As contas de Finanças aparecem no My Planner com essa antecedência.')} />
    </Bloco>

    {Platform.OS !== 'web' && <TesteDeAlerta cor={cor} modo={modo} />}

    {hora && <PickerModal mode="time" cor={cor} value={dataDe(hora === 'de' ? np.de : np.ate)} onClose={() => setHora(null)}
      onPick={d => { mudarNp(hora === 'de' ? { de: hhmm(d) } : { ate: hhmm(d) }); setHora(null); }} />}
  </>);
}

/** Texto 10 (A2): testar o alerta (chega em até 5 segundos, do jeito escolhido) e abrir as configurações do celular. */
function TesteDeAlerta({ cor, modo }: { cor: string; modo: ModoAviso }) {
  const [r, setR] = useState<ResultadoTeste | null>(null);
  const [ocupado, setOcupado] = useState(false);
  return (
    <Bloco titulo={t('Testar e conferir')}>
      <AlvoGuia id="ajustes.avisos.testar">
      <BotaoAcao cor={cor} disabled={ocupado} label={'🔔 ' + t('Testar alerta')} onPress={async () => {
        setOcupado(true);
        try { setR(await testarAlerta(modo)); } finally { setOcupado(false); }
      }} />
      </AlvoGuia>
      {!!r && <ResultadoDoTeste r={r} modo={modo} cor={cor} />}
      <BotaoAcao cor={cor} label={'⚙️ ' + t('Abrir configurações do celular')} onPress={abrirConfiguracoes} />
      <Dica>{t('Se o alerta atrasar ou não aparecer: nas configurações do app, deixe as notificações ligadas, a bateria "sem restrição" e permita "Alarmes e lembretes".')}</Dica>
    </Bloco>
  );
}

/** Texto 07 C1: lista de opções sempre à mostra, ✓ na atual e um ▶ "Ouvir/Sentir" em cada uma que dá para sentir.
 *  rotulo troca o "Sentir" (ex.: "Ouvir" na lista de sons). Alarme (som) e Desligado ficam sem ▶ Sentir.
 *  Usada aqui (Padrão do app, Tipo de vibração) e em Ajustes > Abas > [aba] > Som e vibração. */
export function ListaComSentir<T extends string>({ opcoes, valor, onEscolher, podeSentir, onSentir, cor, rotulo }:
  { opcoes: [T, string][]; valor: T; onEscolher: (v: T) => void; podeSentir: (v: T) => boolean; onSentir: (v: T) => void; cor: string; rotulo?: string }) {
  const m = useTamCaixas();
  return (<>
    {opcoes.map(([v, l], i) => {
      const on = v === valor;
      return (
        <View key={v}>
          {i > 0 && <Divisor />}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Pressable onPress={() => onEscolher(v)} accessibilityRole="radio" accessibilityState={{ checked: on }}
              style={({ pressed }) => ({ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: m.linhaV, opacity: pressed ? 0.6 : 1 })}>
              <Text style={{ width: 18, color: cor, fontWeight: '800', fontSize: 16 }}>{on ? '✓' : ''}</Text>
              <Text style={{ color: C.text, flex: 1, fontWeight: on ? '700' : '500' }}>{l}</Text>
            </Pressable>
            {podeSentir(v) && (
              <Pressable onPress={() => onSentir(v)} hitSlop={8} accessibilityRole="button" accessibilityLabel={(rotulo ?? t('Ouvir/Sentir')) + ': ' + l}
                style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 10, borderRadius: 14,
                  borderWidth: 1, borderColor: cor, backgroundColor: pressed ? alpha(cor, 0.15) : 'transparent' })}>
                <Text style={{ color: cor, fontSize: 12 }}>▶</Text>
                <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{rotulo ?? t('Sentir')}</Text>
              </Pressable>
            )}
          </View>
        </View>
      );
    })}
  </>);
}
